import React from 'react'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { InfoIcon } from '@phosphor-icons/react'
import { get } from 'lodash'
import { useTranslation } from 'react-i18next'

import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import Filter from '../../../Filter/Filter'
import { INTEGRATION_TYPES } from '../helpers'

const PmsIntegrationConfiguration = ({
  values,
  setValues,
  handleChange,
  hasConnectedIntegration,
  pmCompanyOptions,
  propertyOptions,
  fetchProperties,
  setShowModal,
}) => {
  const { t } = useTranslation(['organisation'])
  const hasCompanyOptions = pmCompanyOptions.length > 0

  const getSelectCompanyPlaceholder = (type) =>
    type ? (
      hasCompanyOptions ? (
        t('organisation:places.integrations.pms.companyPlaceholder')
      ) : (
        <React.Fragment>
          {t('organisation:places.integrations.pms.companyNotFound')}
          <InfoIcon className="info" data-tip data-for="company-tip" />
          <ReactTooltip className="tooltip" id="company-tip" place="top" effect="solid">
            <div>{t('organisation:places.integrations.pms.companyNotFoundTooltip')}</div>
          </ReactTooltip>
        </React.Fragment>
      )
    ) : (
      t('organisation:places.integrations.pms.companyPlaceholder')
    )

  const getCompanyFilterPlaceholder = () =>
    values.company && pmCompanyOptions.length
      ? get(
          pmCompanyOptions.find((x) => x.id === values.company),
          'text',
          t('organisation:places.integrations.pms.companyPlaceholder'),
        )
      : getSelectCompanyPlaceholder(values.type)

  const getCompanyFilterValue = () =>
    values.company &&
    pmCompanyOptions.length &&
    pmCompanyOptions.find((x) => x.id === values.company)
      ? [pmCompanyOptions.find((x) => x.id === values.company)]
      : []

  const onCompanyFilterChange = (e) => {
    if (e.target.value) {
      const { id: companyId } = e.target.value
      setValues({ ...values, property: null, company: companyId })
      fetchProperties(values.type, companyId)
    }
  }

  const getPropertyFilterPlaceholder = () =>
    values.property && propertyOptions.length
      ? get(
          propertyOptions.find((x) => String(x.id) === String(values.property)),
          'text',
          t('organisation:places.integrations.pms.property.placeholder'),
        )
      : t('organisation:places.integrations.pms.property.placeholder')

  const getPropertyFilterValue = () =>
    values.property &&
    propertyOptions.length &&
    propertyOptions.find((x) => x.id === values.property)
      ? [propertyOptions.find((x) => x.id === values.property)]
      : []

  const onPropertyFilterChange = (e) => {
    if (e.target.value) setValues({ ...values, property: e.target.value.id })
  }

  return (
    <>
      <Filter
        placeholder={getCompanyFilterPlaceholder()}
        value={getCompanyFilterValue()}
        options={pmCompanyOptions}
        config={{ hasSearch: true }}
        name="company"
        additionalClass="ctp-integration__filter"
        disabled={!hasCompanyOptions || !values.type || !!hasConnectedIntegration}
        onChange={onCompanyFilterChange}
      />
      <span
        className={`input-desc-label ${!values.type || hasConnectedIntegration ? 'disabled' : ''}`}
        onClick={() => setShowModal(true)}
      >
        {t('organisation:places.integrations.pms.addNewCompany')}
      </span>
      {values.type === INTEGRATION_TYPES.REAL_PAGE ? (
        <TextField
          name="property"
          placeholder={t('organisation:places.integrations.pms.propertyId.placeholder')}
          label={t('organisation:places.integrations.pms.propertyId.label')}
          onChange={handleChange}
          value={values.property}
          disabled={!values.company || hasConnectedIntegration}
        />
      ) : (
        <Filter
          name="property"
          placeholder={getPropertyFilterPlaceholder()}
          value={getPropertyFilterValue()}
          options={propertyOptions}
          onChange={onPropertyFilterChange}
          disabled={!propertyOptions.length || !values.company || hasConnectedIntegration}
          config={{ hasSearch: true }}
          additionalClass="ctp-integration__filter"
          label={
            <React.Fragment>
              {t('organisation:places.integrations.pms.property.label')}{' '}
              <InfoIcon data-tip data-for="property-tip" />
              <ReactTooltip className="tooltip" id="property-tip" place="top" effect="solid">
                <div>{t('organisation:places.integrations.pms.property.tooltip')}</div>
              </ReactTooltip>
            </React.Fragment>
          }
        />
      )}
    </>
  )
}

PmsIntegrationConfiguration.propTypes = {
  values: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  hasConnectedIntegration: PropTypes.bool,
  pmCompanyOptions: PropTypes.array,
  propertyOptions: PropTypes.array,
  fetchProperties: PropTypes.func.isRequired,
  setShowModal: PropTypes.func.isRequired,
}

export default PmsIntegrationConfiguration
